import Highcharts from "highcharts";
import HighchartsReact from "highcharts-react-official";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { styled } from "@mui/material/styles";
import {
  ActiveIssuesCaseData,
  ActiveIussesChartOptions as ActiveIssuesChartOptions,
  PointEventWithCaseId
} from "./ActiveIssuesInterfaces";
import { useResizeDetector } from "react-resize-detector";

// Highcharts currently blocks blur events. For example, if you open a dropdown menu and then click on the chart
// you would expect the menu to close automatically (as it does if you click elsewhere) but instead, the onblur is blocked.
//The workaround below is from the following github post:
// https://github.com/highcharts/highcharts/issues/4606

//@ts-ignore
Highcharts.Pointer.prototype.onContainerMouseDown = function(e) {
  e = this.normalize(e);
  //@ts-ignore
  this.zoomOption(e);
  // @ts-ignore
  this.dragStart(e);
};

const options: ActiveIssuesChartOptions = {
  chart: {
    type: "bar"
  },
  title: {
    text: "Losses to Date (MWh)"
  },
  xAxis: {
    categories: []
  },
  yAxis: {
    min: 0,
    title: {
      text: "Losses to Date (MWh)"
    }
  },
  legend: {
    enabled: false
  },
  tooltip: {
    outside: true,
    formatter: function() {
      return `<p><b>Machine:</b> ${
        this.point.name
      }</br><b>Losses:</b> ${this.y?.toFixed(2)}MWh</p>`;
    }
  },
  plotOptions: {
    series: {
      dataLabels: {
        enabled: true,
        inside: true,
        verticalAlign: "middle",
        filter: {
          property: "percentage",
          operator: ">",
          value: 20
        },
        useHTML: true,
        style: {
          color: "000"
        },
        formatter: function() {
          return `<p style="text-align: center; text-shadow:-1px -1px 0 #FFF, 1px -1px 0 #FFF, -1px 1px 0 #FFF, 1px 1px 0 #FFF">${
            this.point.name
          }<br/>${this.point.y?.toFixed(2)}MWh</p>`;
        }
      },
      allowPointSelect: true,
      stacking: "normal",
      states: {
        select: {
          color: "red"
        }
      },
      cursor: "pointer",
      point: {
        events: {
          click: function() {}
        }
      }
    },
    bar: {
      pointWidth: 100,
      colorByPoint: true
    }
  },
  series: [],
  credits: {
    enabled: false
  },
  exporting: {
    chartOptions: {
      plotOptions: {
        series: {
          // Need to override the data label for exporting, as the text-shadows in the data labels cause issues
          dataLabels: {
            style: {
              color: "contrast"
            },
            formatter: function() {
              return `<p>${this.point.name}<br/>${this.point.y}MWh</p>`;
            }
          }
        }
      }
    }
  }
};

export interface ActiveIssueChartProps {
  lostEnergyCases: ActiveIssuesCaseData[] | undefined;
  onChangeSelectedCaseId: (caseId: number) => void;
  selectedCaseId: number | undefined;
}

const isBarSeriesOptions = (
  series: Highcharts.SeriesOptions
): series is Highcharts.SeriesBarOptions => {
  return series.type === "bar";
};

export const ActiveIssuesChart = ({
  lostEnergyCases,
  onChangeSelectedCaseId,
  selectedCaseId
}: ActiveIssueChartProps): JSX.Element => {
  const [chartOptions, setChartOptions] = useState<Highcharts.Options>(options);

  const chartRef = useRef<HighchartsReact.RefObject>(null);

  // Need to manually tell the graph to resize, as it breaks flexbox otherwise.
  const onResize = useCallback(() => {
    chartRef.current?.chart.reflow();
  }, []);

  const { ref: wrapperRef } = useResizeDetector({ onResize });

  // Create list of sites with sums of lost energy and predicted losses from cases.
  const sites = useMemo(() => {
    // The initial value passed in to the reduce method is an empty array.
    // In the reduce method we push sites to the array and then sum the losses_to_date and predicted_losses on each site.
    return lostEnergyCases?.reduce(
      (
        sites: {
          site_name: string;
          site_losses_to_date_mwh: number;
          site_predicted_energy_loss_next_month_mwh: number;
        }[],
        currentValue
      ) => {
        const existingSite = sites.find(
          site => site.site_name === currentValue.site_name
        );
        // If site already added to sites add the losses and predicted losses to date for the individual site, else add site to sites array
        if (existingSite) {
          existingSite.site_losses_to_date_mwh +=
            currentValue.losses_to_date_mwh;
          existingSite.site_predicted_energy_loss_next_month_mwh +=
            currentValue.predicted_energy_loss_next_month_mwh;
        } else {
          sites.push({
            site_name: currentValue.site_name,
            site_losses_to_date_mwh: currentValue.losses_to_date_mwh,
            site_predicted_energy_loss_next_month_mwh:
              currentValue.predicted_energy_loss_next_month_mwh
          });
        }
        return sites;
      },
      []
    );
  }, [lostEnergyCases]);

  const handleClickPoint = useCallback(
    (e: PointEventWithCaseId) => {
      onChangeSelectedCaseId(e.point.caseId);
    },
    [onChangeSelectedCaseId]
  );

  // Create the chart series. Update the series if the data or sites change.
  const series = useMemo(() => {
    if (sites) {
      return sites.map<Highcharts.SeriesBarOptions>(({ site_name }) => {
        const filteredLostEnergyCasesBySite = lostEnergyCases?.filter(
          caseData => caseData.site_name === site_name
        );

        const seriesData = filteredLostEnergyCasesBySite?.map(caseData => ({
          x: sites.findIndex(
            ({ site_name }) => site_name === caseData.site_name
          ),
          y: caseData.losses_to_date_mwh,
          name: caseData.machine_name,
          caseId: caseData.case_id
        }));
        return {
          type: "bar",
          data: seriesData
        };
      });
    }
  }, [lostEnergyCases, sites]);

  // Update the chart options with click point function, series and xAxis.
  useEffect(() => {
    setChartOptions(prevChartOptions => {
      return {
        ...prevChartOptions,
        xAxis: {
          ...prevChartOptions.xAxis,
          categories: sites?.map(
            site =>
              `${site.site_name}</br><b>${site.site_losses_to_date_mwh.toFixed(
                2
              )} MWh</b>` // TODO: This value will need to be updated dynamically from state when we support changing the table view between losses_to_date and predicted_losses.
          )
        },
        plotOptions: {
          ...prevChartOptions.plotOptions,
          series: {
            ...prevChartOptions?.plotOptions?.series,
            point: {
              events: {
                click: e => {
                  handleClickPoint(e as PointEventWithCaseId);
                }
              }
            }
          }
        },
        series: series
      };
    });
  }, [handleClickPoint, series, sites]);

  // Update the series on the chart when the selectedCaseId changes.
  useEffect(() => {
    setChartOptions(prevChartOptions => {
      return {
        ...prevChartOptions,
        series: prevChartOptions?.series
          ?.filter(isBarSeriesOptions)
          .map<Highcharts.SeriesBarOptions>(barSeries => {
            const updatedData = barSeries.data?.map(point => {
              if (point && typeof point !== "number") {
                // Highcharts can't automatically type the caseId on the series point.
                const updatedPoint = (point as unknown) as {
                  caseId: number;
                };
                return {
                  ...point,
                  selected: selectedCaseId === updatedPoint.caseId
                };
              } else {
                return point;
              }
            });
            return { type: "bar", data: updatedData };
          })
      };
    });
  }, [selectedCaseId]);

  return (
    <ChartSection ref={wrapperRef}>
      <HighchartsReact
        ref={chartRef}
        highcharts={Highcharts}
        options={chartOptions}
        containerProps={{ style: { height: "100%" } }}
      />
    </ChartSection>
  );
};

const ChartSection = styled("div")`
  overflow: hidden;
  flex: 1 1 350px;
`;
